import React, { FC, useState } from 'react';
import ReactDOM from 'react-dom';
import { Dropdown, Space, Spin } from 'antd';
import { DownOutlined, LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useLogout } from '~/controllers/auth';
import { useDirection } from '~/hooks/i18nHooks';
import { getDecodedJwt } from '~/utils/getDecodedJwt';
import { UaeLogoutIcon, UaeLogoutSvgIcon } from '~/img';

/* ─── Dropdown overlay card ─────────────────────────────────── */
const LogoutOverlay: FC = () => {
  const [t] = useTranslation('common');
  const { handleLogout } = useLogout();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const direction = useDirection();
  const isRtl = direction === 'rtl';

  const onLogoutClick = async () => {
    setIsLoggingOut(true);
    try {
      await handleLogout();
    } finally {
      // redirect happens inside handleLogout; this is a safety fallback
      setIsLoggingOut(false);
    }
  };

  return (
    <>
      <OverlayCard>
        <LogoutRow id="uaeHeaderLogout-logoutAction" onClick={onLogoutClick} isRtl={isRtl}>
          <StyledLogoutIcon />
          <LogoutLabel isRtl={isRtl}>{t('Logout')}</LogoutLabel>
        </LogoutRow>
      </OverlayCard>

      {/* Full-screen loading portal — shows while logout API call is in-flight */}
      {isLoggingOut &&
        ReactDOM.createPortal(
          <LogoutLoadingOverlay>
            <Spin indicator={<StyledLoadingIcon spin />} />
          </LogoutLoadingOverlay>,
          document.body
        )}
    </>
  );
};

/* ─── JWT name resolution ───────────────────────────────────── */
const getUserName = (): string => {
  const token = getDecodedJwt();
  if (!token) return '';
  return token.name || token.given_name || token.preferred_username || token.email?.split('@')[0] || '';
};

/**
 * UAE Self-Onboarding Portal — desktop header logout dropdown.
 * Shows the logged-in user's name with a caret that reveals a Logout card.
 * Intentionally separate from HeaderIdentityMenu to avoid affecting Agent/Sales portals.
 */
const UaeHeaderLogout: FC = () => {
  const userName = getUserName();
  const [open, setOpen] = useState(false);

  if (!userName) return null;

  return (
    <Container className="UaeHeaderLogout">
      <Dropdown
        trigger={['click']}
        overlay={<LogoutOverlay />}
        placement="bottomRight"
        visible={open}
        onVisibleChange={setOpen}
        overlayStyle={{ top: "75px" }}
        align={{ offset: [0, 25] }}
      >
        <TriggerArea size={6}>
          <UserNameText title={userName}>
            {userName.length > 15 ? `${userName.slice(0, 15)}...` : userName}
          </UserNameText>
          <CaretIcon open={open} />
        </TriggerArea>
      </Dropdown>
    </Container>
  );
};

/* ─── Styled Components ─────────────────────────────────────── */

const Container = styled.div`
  cursor: pointer;
`;

const TriggerArea = styled(Space)`
  align-items: center;
`;

interface CaretProps {
  open: boolean;
}

const CaretIcon = styled(DownOutlined) <CaretProps>`
  font-size: 11px;
  color: #1b1b20;
  transition: transform 0.2s ease;
  transform: ${({ open }) => (open ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const UserNameText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #1b1b20;
  white-space: nowrap;
`;

const OverlayCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 32px 64px -12px rgba(16, 24, 40, 0.14);
  width: 341px;
  padding: 8px;
  overflow: hidden;
  &:hover {
    background: #f5f7fa;
  }

`;

interface LogoutRowProps {
  isRtl?: boolean;
}

const LogoutRow = styled.div<LogoutRowProps>`
  display: flex;
  flex-direction: ${({ isRtl }) => (isRtl ? 'row-reverse' : 'row')};
  width: 341px;
  height: 56px;
  padding: 8px 16px;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: background 0.15s ease;
  box-sizing: border-box;

  svg {
    flex-shrink: 0;
  }
`;

const LogoutLabel = styled.span<LogoutRowProps>`
  font-size: 14px;
  font-weight: 400;
  color: #1b1b20;
  font-family: ${({ isRtl }) => (isRtl ? 'Tajawal' : 'Inter')}, sans-serif;
`;

const StyledLogoutIcon = styled(UaeLogoutSvgIcon)`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  aspect-ratio: 1 / 1;
`;



const LogoutLoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(255, 255, 255, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledLoadingIcon = styled(LoadingOutlined)`
  font-size: 40px;
  color: #0060a8;
`;

export default UaeHeaderLogout;

